<template>
    <div class="rent-view-container">
        <!-- Header Section -->
        <div class="header-section">
            <div class="header-content">
                <div class="title-section">
                    <div class="title-icon">
                        <img src="@/assets/rupeeSymbol.svg" alt="Rupee icon" class="logo-image" />
                    </div>
                    <div class="title-text">
                        <h1>Rent Payments</h1>
                        <p>Track and manage all payment transactions</p>
                    </div>
                </div>
                <button class="back-btn" @click="goBack">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5m0 0l7 7m-7-7l7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Back
                </button>
            </div>
        </div>

        <!-- Filters and Search Section -->
        <div class="filters-section">
            <div class="search-container">
                <div class="search-input-wrapper">
                    <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
                        <path d="m21 21-4.35-4.35" stroke="currentColor" stroke-width="2"/>
                    </svg>
                    <input 
                        type="text" 
                        class="search-input" 
                        placeholder="Search by mobile number"
                        v-model="searchQuery"
                        @input="handleSearch"
                    />
                </div>
            </div>
            <div class="filter-controls">
                <select class="filter-select" v-model="statusFilter" @change="handleFilterChange">
                    <option value="">All Status</option>
                    <option value="PAID">Paid</option>
                    <option value="DUE">Due</option>
                </select>
                <select class="filter-select" v-model="yearFilter" @change="handleFilterChange">
                    <option value="">All Years</option>
                    <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                </select>
            </div>
        </div>

        <!-- Stats Cards -->
        <div class="stats-section">
            <div class="stat-card">
                <div class="stat-icon paid">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ paidCount }}</h3>
                    <p>Paid</p>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon due">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </div>
                <div class="stat-content">
                    <h3>{{ dueCount }}</h3>
                    <p>Due</p>
                </div>
            </div>
            <div class="stat-card">
                <div class="stat-icon total">
                    <img src="@/assets/rupeeSymbol.svg" alt="Rupee icon" class="logo-image" />
                </div>
                <div class="stat-content">
                    <h3>{{ totalAmount }}</h3>
                    <p>Total Amount</p>
                </div>
            </div>
        </div>

        <!-- Table Section -->
        <div class="table-section">
            <div class="table-header">
                <h2>Payment Records</h2>
                <div class="table-actions">
                    <button class="export-btn" @click="exportData">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" stroke-width="2"/>
                        </svg>
                        Export
                    </button>
                </div>
            </div>
            
            <div class="modern-table-container" :class="{ 'table-loading': isLoadingTableData }">
                <!-- Table Loading Overlay -->
                <div class="table-loading-overlay" v-if="isFetching">
                    <div class="table-spinner">
                        <div class="spinner-circle-small"></div>
                        <span class="loading-text-small">Loading payments...</span>
                    </div>
                </div>
                <div class="table-wrapper">
                    <table class="modern-table">
                        <thead v-if="filteredRents.length > 0 && !isFetching">
                            <tr>
                                <th class="sortable" @click="sortBy('rentpaymentid')">
                                    Payment ID
                                    <svg class="sort-icon" width="16" height="16" viewBox="0 0 24 24">
                                        <path d="M7 14l5-5 5 5z" fill="currentColor"/>
                                    </svg>
                                </th>
                                <th>Guest Name</th>
                                <th>Mobile Number</th>
                                <th>Object</th>
                                <th class="sortable" @click="sortBy('amount')">
                                    Amount
                                    <svg class="sort-icon" width="16" height="16" viewBox="0 0 24 24">
                                        <path d="M7 14l5-5 5 5z" fill="currentColor"/>
                                    </svg>
                                </th>
                                <th>Period</th>
                                <th>Payment Mode</th>
                                <th>Status</th>
                                <th class="sortable" @click="sortBy('paidon')">
                                    Paid On
                                    <svg class="sort-icon" width="16" height="16" viewBox="0 0 24 24">
                                        <path d="M7 14l5-5 5 5z" fill="currentColor"/>
                                    </svg>
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="rent in filteredRents" :key="rent.rentpaymentid" class="table-row">
                                <td class="payment-id">
                                    <span class="id-badge">{{ rent.rentpaymentid }}</span>
                                </td>
                                <td class="guest-info">
                                    <div class="guest-avatar">
                                        {{ getInitials(rent.firstName || rent.objectName) }}
                                    </div>
                                    <div class="guest-details">
                                        <span class="guest-name">{{ rent.firstName || 'Unknown' }}</span>
                                    </div>
                                </td>
                                <td class="mobile-cell">
                                    <span class="mobile-number">{{ rent.mobileno || '--' }}</span>
                                </td>
                                <td>
                                    <span class="object-tag">{{ rent.objectId || '--' }}</span>
                                </td>
                                <td class="amount-cell">
                                    <span class="amount">{{ formatCurrency(rent.amount) }}</span>
                                </td>
                                <td class="period-cell">
                                    <span class="period">{{ formatPeriod(rent.month, rent.year) }}</span>
                                </td>
                                <td>
                                    <span class="payment-mode">{{ rent.paymentMode || '--' }}</span>
                                </td>
                                <td>
                                    <span :class="['modern-status-badge', getStatusClass(rent.status)]">
                                        <span class="status-dot"></span>
                                        {{ rent.status || 'Unknown' }}
                                    </span>
                                </td>
                                <td class="date-cell">
                                    {{ formatDate(rent.paidon) || '--' }}
                                </td>
                                <td class="actions-cell">
                                    <div class="action-dropdown-container">
                                        <button class="modern-action-btn" @click="toggleActionMenu(rent.rentpaymentid, $event)">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="1" fill="currentColor"/>
                                                <circle cx="12" cy="5" r="1" fill="currentColor"/>
                                                <circle cx="12" cy="19" r="1" fill="currentColor"/>
                                            </svg>
                                        </button>
                                        <div v-if="openActionMenu === rent.rentpaymentid" class="modern-action-dropdown">
                                            <button class="action-item paid" @click="updateRentStatus(rent.rentpaymentid, 'PAID')">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2"/>
                                                </svg>
                                                Mark as Paid
                                            </button>
                                            <button class="action-item due" @click="updateRentStatus(rent.rentpaymentid, 'DUE')">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2"/>
                                                </svg>
                                                Mark as Due
                                            </button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <!-- Empty State -->
                    <div v-if="filteredRents.length === 0 && !isFetching" class="empty-state">
                        <div class="empty-icon">
                            <img src="@/assets/rent.svg" alt="No payments illustration" class="empty-illustration-img" />
                        </div>
                        <h3>No payments found</h3>
                        <p>Try adjusting your search or filter criteria</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modern Pagination -->
        <div class="modern-pagination" v-if="totalCount > pageSize">
            <div class="pagination-info">
                <span>Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} results</span>
            </div>
            <div class="pagination-controls">
                <button 
                    :disabled="currentPage === 1 || isFetching || isLoadingTableData" 
                    @click="changePage(currentPage - 1)"
                    class="pagination-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </button>
                
                <div class="page-numbers">
                    <button 
                        v-for="page in visiblePages" 
                        :key="page"
                        :class="['page-btn', { 'active': page === currentPage, 'ellipsis': page === '...' }]" 
                        :disabled="isFetching || isLoadingTableData || page === '...'"
                        @click="changePage(page)">
                        {{ page }}
                    </button>
                </div>
                
                <button 
                    :disabled="currentPage === totalPages || isFetching || isLoadingTableData" 
                    @click="changePage(currentPage + 1)"
                    class="pagination-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';

export default {
    name: 'RentView',
    components: {
    },
    props: {
        objectSpaces: {
            type: Array,
            required: true,
        },
        objectData: {
            type: Object,
            required: true,
        },
        isLoading: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            objectName: '',
            adminProfileData: {},
            rents: [],
            currentPage: 1,
            pageSize: this.getInitialPageSize(),
            totalCount: 0,
            totalPages: 0,
            isFetching: false,
            isLoadingTableData: false,
            objectId: '',
            cacheDuration: 5 * 60 * 1000, // Cache duration: 5 minutes in milliseconds
            openActionMenu: null, // Track which menu is open
            searchQuery: '',
            statusFilter: '',
            yearFilter: '',
            sortField: '',
            sortDirection: 'asc',
            searchDebounceTimer: null,
            resizeTimer: null,
        };
    },
    computed: {
        visiblePages() {
            const range = 2;
            let pages = [];
            let start = Math.max(1, this.currentPage - range);
            let end = Math.min(this.totalPages, this.currentPage + range);

            if (start > 2) pages.push('...');
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            if (end < this.totalPages - 1) pages.push('...');

            if (!pages.includes(1) && start > 1) pages.unshift(1);
            if (!pages.includes(this.totalPages) && end < this.totalPages) pages.push(this.totalPages);

            return pages;
        },
        filteredRents() {
            let filtered = [...this.rents];

            // Apply search filter
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(rent => 
                    (rent.rentpaymentid && rent.rentpaymentid.toString().toLowerCase().includes(query)) ||
                    (rent.firstName && rent.firstName.toLowerCase().includes(query)) ||
                    (rent.objectName && rent.objectName.toLowerCase().includes(query)) ||
                    (rent.amount && rent.amount.toString().includes(query)) ||
                    (rent.mobileno && rent.mobileno.toString().includes(query)) ||
                    (rent.guestId && rent.guestId.toString().toLowerCase().includes(query))
                );
            }

            // Apply status filter
            if (this.statusFilter) {
                filtered = filtered.filter(rent => rent.status === this.statusFilter);
            }

            // Apply year filter
            if (this.yearFilter) {
                filtered = filtered.filter(rent => rent.year === parseInt(this.yearFilter));
            }

            // Apply sorting
            if (this.sortField) {
                filtered.sort((a, b) => {
                    let aVal = a[this.sortField];
                    let bVal = b[this.sortField];
                    
                    if (this.sortField === 'amount') {
                        aVal = parseFloat(aVal) || 0;
                        bVal = parseFloat(bVal) || 0;
                    } else if (this.sortField === 'paidon' || this.sortField === 'createdat') {
                        aVal = new Date(aVal);
                        bVal = new Date(bVal);
                    } else {
                        aVal = aVal?.toString().toLowerCase() || '';
                        bVal = bVal?.toString().toLowerCase() || '';
                    }

                    if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
                    if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
                    return 0;
                });
            }

            return filtered;
        },
        availableYears() {
            const years = [...new Set(this.rents.map(rent => rent.year).filter(Boolean))];
            return years.sort((a, b) => b - a);
        },
        paidCount() {
            return this.rents.filter(rent => rent.status === 'PAID').length;
        },
        dueCount() {
            return this.rents.filter(rent => rent.status === 'DUE').length;
        },
        totalAmount() {
            const total = this.rents.reduce((sum, rent) => sum + (parseFloat(rent.amount) || 0), 0);
            return this.formatCurrency(total);
        },
    },
    mounted() {
        this.objectName = this.$route.params.objectName;
        this.objectId = this.$route.params.id;
        let adminProfileData = localStorage.getItem('ADMIN_PROFILE_DATA');
        this.adminProfileData = JSON.parse(adminProfileData);
        // Force fresh API call on mount (page refresh)
        window.addEventListener('beforeunload', this.clearCache);
        // Update page size based on screen size
        this.updatePageSize();
        window.addEventListener('resize', this.handleResize);
        this.fetchRents();
        document.addEventListener('click', this.handleClickOutside);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
    },
    beforeUnmount() {
        // Clean up the event listener
        window.removeEventListener('beforeunload', this.clearCache);
        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener('click', this.handleClickOutside);
        window.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        goBack() {
            this.$router.go(-1);
        },
        getStatusStyle(status) {
            if (status === 'DUE') {
                return { backgroundColor: '#F88108', color: '#fff', fontWeight: 'bold' };
            }
            if (status === 'PAID') {
                return { backgroundColor: '#12D419', color: '#fff', fontWeight: 'bold' };
            }
            return {};
        },
        handleAddRent(formData) {
            // Emit the form data to the parent component
            this.$emit('addRent', formData);
        },
        handleRentOnboarding(formData) {
            // Emit the form data to the parent component
            this.$emit('showOnboardAddForm', formData);
        },
        clearCache() {
            // Clear all cache entries for this object
            if (this.objectId) {
                const keys = Object.keys(localStorage);
                keys.forEach(key => {
                    if (key.startsWith(`RENT-`) && key.includes(`-${this.objectId}`)) {
                        localStorage.removeItem(key);
                    }
                });
            }
        },
        async fetchRentsData(forceFresh = false) {
            const cacheKey = `RENT-${this.currentPage}-${this.pageSize}-${this.objectId}`;
            const cachedData = localStorage.getItem(cacheKey);

            // Skip cache if forceFresh is true (e.g., on page refresh)
            if (!forceFresh && cachedData) {
                const { data, timestamp } = JSON.parse(cachedData);
                const now = new Date().getTime();
                if (now - timestamp < this.cacheDuration) {
                    // Use cached data
                    this.rents = data.data;
                    this.totalCount = data.totalCount;
                    this.totalPages = Math.ceil(this.totalCount / this.pageSize);
                    return;
                }
            }

            // Fetch from API
            try {
                this.isLoadingTableData = true;
                // Build query parameters
                const params = {
                    objectId: this.objectId,
                    page: this.currentPage,
                    pagesize: this.pageSize
                };
                
                // Add mobile number search if searchQuery looks like a phone number or if user wants to search by mobile
                if (this.searchQuery && (this.searchQuery.match(/^\d+$/) || this.searchQuery.length >= 10)) {
                    params.guestMobileno = this.searchQuery;
                }
                
                const response = await apiService.getRentPayments(params);

                // Store response in cache with timestamp
                const cacheData = {
                    data: response.data,
                    timestamp: new Date().getTime(),
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));

                // Update component data
                this.rents = response.data.data;
                this.totalCount = response.data.totalCount;
                this.totalPages = Math.ceil(this.totalCount / this.pageSize);
            } catch (error) {
                // Handle "no data" case - set empty arrays without showing error
                if (isNoDataError(error)) {
                    this.rents = [];
                    this.totalCount = 0;
                    this.totalPages = 0;
                } else if (isHmsSubscriptionInactiveError(error)) {
                    // Subscription inactive — modal shown in App.vue
                } else {
                    console.error('Error fetching rent payment:', error);
                    // Handle error (e.g., show error message)
                }
            } finally {
                this.isLoadingTableData = false;
            }
        },
        async fetchRents(forceFresh = false) {
            const cacheKey = `RENT-${this.currentPage}-${this.pageSize}-${this.objectId}`;
            const cachedData = localStorage.getItem(cacheKey);

            // Skip cache if forceFresh is true (e.g., on page refresh)
            if (!forceFresh && cachedData) {
                const { data, timestamp } = JSON.parse(cachedData);
                const now = new Date().getTime();
                if (now - timestamp < this.cacheDuration) {
                    // Use cached data
                    this.rents = data.data;
                    this.totalCount = data.totalCount;
                    this.totalPages = Math.ceil(this.totalCount / this.pageSize);
                    this.isFetching = false;
                    return;
                }
            }

            // Fetch from API
            try {
                this.isFetching = true;
                // Build query parameters
                const params = {
                    objectId: this.objectId,
                    page: this.currentPage,
                    pagesize: this.pageSize
                };
                
                // Add mobile number search if searchQuery looks like a phone number or if user wants to search by mobile
                if (this.searchQuery && (this.searchQuery.match(/^\d+$/) || this.searchQuery.length >= 10)) {
                    params.guestMobileno = this.searchQuery;
                }
                
                const response = await apiService.getRentPayments(params);

                // Store response in cache with timestamp
                const cacheData = {
                    data: response.data,
                    timestamp: new Date().getTime(),
                };
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));

                // Update component data
                this.rents = response.data.data;
                this.totalCount = response.data.totalCount;
                this.totalPages = Math.ceil(this.totalCount / this.pageSize);
            } catch (error) {
                // Handle "no data" case - set empty arrays without showing error
                if (isNoDataError(error)) {
                    this.rents = [];
                    this.totalCount = 0;
                    this.totalPages = 0;
                } else if (isHmsSubscriptionInactiveError(error)) {
                    // Subscription inactive — modal shown in App.vue
                } else {
                    console.error('Error fetching rent payment:', error);
                    // Handle error (e.g., show error message)
                }
            } finally {
                this.isFetching = false;
            }
        },
        getInitialPageSize() {
            // Set initial page size based on screen width
            if (typeof window !== 'undefined') {
                return window.innerWidth >= 768 ? 50 : 10;
            }
            return 50; // Default to desktop size
        },
        updatePageSize() {
            const newPageSize = window.innerWidth >= 768 ? 50 : 10;
            if (this.pageSize !== newPageSize) {
                this.pageSize = newPageSize;
                this.currentPage = 1; // Reset to first page when page size changes
                // Clear all cache entries when page size changes
                if (this.objectId) {
                    const keys = Object.keys(localStorage);
                    keys.forEach(key => {
                        if (key.startsWith(`RENT-`) && key.includes(`-${this.objectId}`)) {
                            localStorage.removeItem(key);
                        }
                    });
                }
            }
        },
        handleResize() {
            // Debounce resize handler
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                const oldPageSize = this.pageSize;
                this.updatePageSize();
                // Only refetch if page size actually changed
                if (oldPageSize !== this.pageSize) {
                    this.fetchRents(true); // Force fresh fetch
                }
            }, 300);
        },
        changePage(page) {
            if (
                (typeof page === 'number' && page >= 1 && page <= this.totalPages && !this.isFetching && !this.isLoadingTableData) ||
                (page === '...' && !this.isFetching && !this.isLoadingTableData)
            ) {
                this.currentPage = page;
                this.fetchRents(true); // Force fresh fetch when changing pages
            }
        },
        formatDate(dateString) {
            if (!dateString) return '--';
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        toggleActionMenu(id, event) {
            // Prevent event from bubbling up and potentially interfering with scroll
            if (event) {
                event.stopPropagation();
                
                if (this.openActionMenu === id) {
                    this.openActionMenu = null;
                } else {
                    this.openActionMenu = id;
                    
                    // Calculate position for fixed dropdown
                    this.$nextTick(() => {
                        const dropdown = document.querySelector('.modern-action-dropdown');
                        if (dropdown && event.target) {
                            const buttonRect = event.target.getBoundingClientRect();
                            dropdown.style.top = `${buttonRect.bottom + 4}px`;
                            dropdown.style.left = `${buttonRect.right - 160}px`; // 160px is min-width
                        }
                    });
                }
            } else {
                this.openActionMenu = this.openActionMenu === id ? null : id;
            }
        },
        async updateRentStatus(rentpaymentid, status) {
            this.openActionMenu = null;
            try {
                this.isFetching = true;
                await apiService.updateRentPayment(rentpaymentid, { status });
                this.clearCache(); // Clear cache to force fresh fetch
                this.fetchRents(true); // Force fresh fetch
            } catch (error) {
                console.error('Error updating rent status:', error);
                // Optionally show error to user
            } finally {
                this.isFetching = false;
            }
        },
        handleClickOutside(event) {
            // Close action menu if clicked outside
            if (!event.target.closest('.modern-action-btn') && !event.target.closest('.modern-action-dropdown')) {
                this.openActionMenu = null;
            }
        },
        handleScroll() {
            // Close dropdown when scrolling to ensure smooth scroll experience
            if (this.openActionMenu) {
                this.openActionMenu = null;
            }
        },
        handleSearch() {
            // Debounce search to avoid too many API calls
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.currentPage = 1; // Reset to first page when searching
                
                // If search looks like a mobile number (digits only and reasonable length), 
                // trigger API call to search by mobile number
                if (this.searchQuery && (this.searchQuery.match(/^\d+$/) || this.searchQuery.length >= 10)) {
                    this.clearCache(); // Clear cache to force fresh API call
                    this.fetchRentsData(); // Only fetch table data, don't refresh entire component
                } else if (!this.searchQuery) {
                    // If search is cleared, fetch fresh data
                    this.clearCache();
                    this.fetchRentsData();
                }
                // For other searches, rely on client-side filtering (filteredRents computed property)
            }, 300);
        },
        handleFilterChange() {
            this.currentPage = 1; // Reset to first page when filtering
        },
        sortBy(field) {
            if (this.sortField === field) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortField = field;
                this.sortDirection = 'asc';
            }
        },
        getStatusClass(status) {
            switch (status?.toUpperCase()) {
                case 'PAID':
                    return 'status-paid';
                case 'DUE':
                    return 'status-due';
                default:
                    return 'status-unknown';
            }
        },
        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(word => word.charAt(0))
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },
        formatCurrency(amount) {
            if (!amount && amount !== 0) return '--';
            
            // Use English locale to avoid Nepali conversion on small screens
            const numAmount = parseFloat(amount);
            const formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'NPR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
            
            // Fallback formatting if NPR is not supported
            try {
                return formatter.format(numAmount);
            } catch (e) {
                return `NPR ${numAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
            }
        },
        formatPeriod(month, year) {
            if (!month || !year) return '--';
            const monthNames = [
                'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
            ];
            const monthIndex = parseInt(month) - 1;
            const monthName = monthNames[monthIndex] || month;
            return `${monthName} ${year}`;
        },
        exportData() {
            // Simple CSV export functionality
            const headers = [
                'Payment ID', 'Guest Name', 'Object ID', 'Amount', 
                'Currency', 'Month', 'Year', 'Status', 'Paid On', 'Created At'
            ];
            
            const csvContent = [
                headers.join(','),
                ...this.filteredRents.map(rent => [
                    rent.rentpaymentid,
                    `"${rent.objectName || ''}"`,
                    rent.objectId || '',
                    rent.amount || '',
                    rent.currency || '',
                    rent.month || '',
                    rent.year || '',
                    rent.status || '',
                    rent.paidon || '',
                    rent.createdat || ''
                ].join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `rent-payments-${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        },
    },
};
</script>

<style scoped>
/* Main Container */
.rent-view-container {
    padding: 0;
    background: #f8fafc;
    height: calc(100vh - 60px); /* Account for DashboardHeader */
    position: relative;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

@media (max-width: 768px) {
    .rent-view-container {
        height: calc(100vh - 60px);
        overflow: hidden;
    }
}

@media (max-width: 480px) {
    .rent-view-container {
        height: auto;
        min-height: calc(100vh - 60px);
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
    }
}

/* Loading Overlay */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    border-radius: 16px;
}

.modern-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.spinner-circle {
    width: 48px;
    height: 48px;
    border: 3px solid #e2e8f0;
    border-top: 3px solid #5b2e8e;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loading-text {
    color: #6b7280;
    font-size: 16px;
    font-weight: 500;
    margin: 0;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Header Section */
.header-section {
    background: white;
    border-radius: 0;
    padding: 16px 24px;
    margin-bottom: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: none;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 100;
    flex-shrink: 0;
    margin-top: 20px;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.title-section {
    display: flex;
    align-items: center;
    gap: 12px;
}

.title-icon {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
}

.logo-image {
    width: 20px;
    height: 20px;
    filter: brightness(0) saturate(100%) invert(100%);
    object-fit: contain;
}

.title-text {
    text-align: left;
}

.title-text h1 {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    letter-spacing: -0.025em;
    text-align: left;
}

.title-text p {
    font-size: 14px;
    color: #64748b;
    margin: 0;
    font-weight: 400;
    text-align: left;
}

.back-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: transparent;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #64748b;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.back-btn:hover {
    border-color: #5b2e8e;
    color: #5b2e8e;
    background: #f8fafc;
}

/* Filters Section */
.filters-section {
    display: flex;
    gap: 12px;
    margin-bottom: 0;
    flex-wrap: wrap;
    padding: 16px 24px;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.search-container {
    flex: 1;
    min-width: 250px;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 12px;
    color: #94a3b8;
    z-index: 1;
    width: 18px;
    height: 18px;
}

.search-input {
    width: 100%;
    padding: 10px 14px 10px 40px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    background: #f8fafc;
    transition: all 0.2s ease;
    outline: none;
}

.search-input:focus {
    border-color: #5b2e8e;
    background: white;
    box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.filter-controls {
    display: flex;
    gap: 10px;
}

.filter-select {
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    font-size: 14px;
    font-weight: 500;
    color: #1e293b;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;
    min-width: 130px;
}

.filter-select:focus {
    border-color: #5b2e8e;
    background: white;
    box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

/* Stats Section */
.stats-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
    margin-bottom: 0;
    padding: 16px 24px;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.stat-card {
    background: white;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
}

.stat-icon svg {
    width: 20px;
    height: 20px;
}

.stat-icon.paid {
    background: linear-gradient(135deg, #10b981, #059669);
}

.stat-icon.due {
    background: linear-gradient(135deg, #f59e0b, #d97706);
}

.stat-icon.total {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
}

.stat-content {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.stat-content h3 {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 2px 0;
    line-height: 1.2;
    text-align: left;
}

.stat-content p {
    font-size: 12px;
    color: #64748b;
    margin: 0;
    font-weight: 500;
    text-align: left;
}

/* Table Section */
.table-section {
    background: white;
    border-radius: 0;
    box-shadow: none;
    border: none;
    overflow: hidden;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.table-header {
    padding: 16px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
}

.table-header h2 {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.export-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.export-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.export-btn svg {
    width: 16px;
    height: 16px;
}

.modern-table-container {
    overflow-x: auto;
    overflow-y: auto;
    position: relative;
    flex: 1;
    min-height: 0;
    width: 100%;
    -webkit-overflow-scrolling: touch;
}

.modern-table-container.table-loading {
    opacity: 0.7;
}

/* Desktop: Allow table to scroll if needed but maximize space */
@media (min-width: 769px) {
    .modern-table-container {
        overflow-y: auto;
        max-height: none;
    }
    
    .table-section {
        overflow: hidden;
        flex: 1;
        min-height: 0;
    }
}

/* Table Loading Overlay */
.table-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(2px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    border-radius: 8px;
}

.table-spinner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.spinner-circle-small {
    width: 20px;
    height: 20px;
    border: 2px solid #e2e8f0;
    border-top: 2px solid #5b2e8e;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.loading-text-small {
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    margin: 0;
}

.table-wrapper {
    min-width: 800px;
    width: 100%;
}

.modern-table {
    width: 100%;
    border-collapse: collapse;
}

.modern-table th {
    background: #f8fafc;
    padding: 12px 16px;
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    color: #475569;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    z-index: 10;
    white-space: nowrap;
    vertical-align: middle;
}

.modern-table th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
    position: relative;
    padding-right: 32px;
}

.modern-table th.sortable:hover {
    background: #f1f5f9;
    color: #5b2e8e;
}

.sort-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 0;
    opacity: 0.5;
    transition: opacity 0.2s ease;
    width: 16px;
    height: 16px;
    display: inline-block;
}

.modern-table th.sortable:hover .sort-icon {
    opacity: 1;
}

.modern-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 14px;
    color: #1e293b;
    white-space: nowrap;
    vertical-align: middle;
    text-align: left;
}

.table-row {
    transition: background-color 0.2s ease;
}

.table-row:hover {
    background: #f8fafc;
}

/* Table Cell Styles */
.payment-id {
    text-align: left;
    vertical-align: middle;
}

.payment-id .id-badge {
    background: #f3f4f6;
    color: #6b7280;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Monaco', monospace;
    display: inline-block;
}

.guest-info {
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: nowrap;
    text-align: left;
    vertical-align: middle;
}

.guest-avatar {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    color: white;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
}

.guest-details {
    display: flex;
    flex-direction: column;
}

.guest-name {
    font-weight: 600;
    color: #1f2937;
}

.guest-id {
    font-size: 12px;
    color: #6b7280;
}

.mobile-cell {
    font-weight: 500;
    text-align: left;
    vertical-align: middle;
}

.mobile-number {
    color: #374151;
    font-family: 'Monaco', monospace;
    font-size: 14px;
    background: #f3f4f6;
    padding: 4px 8px;
    border-radius: 6px;
    font-weight: 600;
    display: inline-block;
}

.object-tag {
    background: #ede9fe;
    color: #7c3aed;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    display: inline-block;
}

.amount-cell {
    text-align: left;
    vertical-align: middle;
}

.amount {
    font-weight: 700;
    color: #059669;
    font-size: 16px;
    display: inline-block;
}

.period-cell {
    text-align: left;
    vertical-align: middle;
}

.period {
    background: #fef3c7;
    color: #d97706;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    display: inline-block;
}

.payment-mode {
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    display: inline-block;
}

.date-cell {
    text-align: left;
    vertical-align: middle;
}

.actions-cell {
    text-align: left;
    vertical-align: middle;
}

/* Status Badges */
.modern-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    vertical-align: middle;
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
}

.status-paid {
    background: #d1fae5;
    color: #065f46;
}

.status-paid .status-dot {
    background: #10b981;
}

.status-due {
    background: #fef3c7;
    color: #92400e;
}

.status-due .status-dot {
    background: #f59e0b;
}

.status-unknown {
    background: #f3f4f6;
    color: #6b7280;
}

.status-unknown .status-dot {
    background: #9ca3af;
}

/* Action Buttons */
.action-dropdown-container {
    position: relative;
    pointer-events: auto;
}

.modern-action-btn {
    width: 36px;
    height: 36px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.modern-action-btn:hover {
    background: #5b2e8e;
    color: white;
    border-color: #5b2e8e;
}

.modern-action-dropdown {
    position: fixed;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 1000;
    min-width: 160px;
    overflow: hidden;
    pointer-events: auto;
}

.action-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 12px 16px;
    background: none;
    border: none;
    text-align: left;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.action-item:hover {
    background: #f8fafc;
}

.action-item.paid:hover {
    background: #ecfdf5;
    color: #065f46;
}

.action-item.due:hover {
    background: #fffbeb;
    color: #92400e;
}

/* Empty State */
.empty-state {
    padding: 60px 20px;
    text-align: center;
    color: #6b7280;
}

.empty-icon {
    margin-bottom: 16px;
    opacity: 0.5;
    display: flex;
    justify-content: center;
    align-items: center;
}

.empty-illustration-img {
    width: 200px;
    height: 200px;
    max-width: 100%;
    object-fit: contain;
}

.empty-state h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: #374151;
}

.empty-state p {
    font-size: 14px;
    margin: 0;
}

/* Modern Pagination */
.modern-pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    background: white;
    border-top: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.pagination-info {
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 4px;
}

.pagination-arrow {
    width: 40px;
    height: 40px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #5b2e8e;
    color: #5b2e8e;
}

.pagination-arrow:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-numbers {
    display: flex;
    gap: 4px;
    margin: 0 8px;
}

.page-btn {
    width: 40px;
    height: 40px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #374151;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled):not(.active):not(.ellipsis) {
    background: #f8fafc;
    border-color: #5b2e8e;
    color: #5b2e8e;
}

.page-btn.active {
    background: linear-gradient(135deg, #5b2e8e, #7c3aed);
    border-color: #5b2e8e;
    color: white;
}

.page-btn.ellipsis {
    cursor: default;
    border: none;
    background: none;
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 1024px) {
    .filters-section,
    .stats-section {
        padding: 12px 16px;
    }
    
    .stats-section {
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 10px;
    }
    
    .stat-card {
        padding: 12px 14px;
    }
}

@media (max-width: 768px) {
    .filters-section,
    .stats-section {
        padding: 12px;
    }
    
    .header-section {
        padding: 16px;
    }
    
    .header-content {
        flex-direction: row;
        gap: 12px;
        align-items: center;
        justify-content: space-between;
    }
    
    .title-section {
        flex: 1;
        min-width: 0;
        gap: 12px;
    }
    
    .title-icon {
        width: 40px;
        height: 40px;
        flex-shrink: 0;
    }
    
    .title-text {
        min-width: 0;
        flex: 1;
    }
    
    .title-text h1 {
        font-size: 20px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    
    .title-text p {
        display: none;
    }
    
    .back-btn {
        padding: 10px 16px;
        font-size: 14px;
        flex-shrink: 0;
    }
    
    .back-btn svg {
        width: 18px;
        height: 18px;
    }
    
    .filters-section {
        flex-direction: column;
    }
    
    .search-container {
        min-width: auto;
    }
    
    .filter-controls {
        flex-wrap: wrap;
    }
    
    .stats-section {
        grid-template-columns: 1fr;
    }
    
    .table-header {
        flex-direction: column;
        gap: 16px;
        align-items: stretch;
        padding: 16px;
    }
    
    .modern-table-container {
        overflow-x: auto;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        display: block;
    }
    
    .table-wrapper {
        min-width: 800px;
        display: table;
    }
    
    .modern-table {
        display: table;
        width: 100%;
    }
    
    .modern-pagination {
        flex-direction: column;
        gap: 16px;
        padding: 16px;
    }
    
    .pagination-info {
        text-align: center;
    }
}

@media (max-width: 640px) {
    .header-section {
        padding: 12px;
        margin-top: 10px;
    }
    
    .header-content {
        gap: 8px;
    }
    
    .title-icon {
        width: 36px;
        height: 36px;
    }
    
    .logo-image {
        width: 20px;
        height: 20px;
    }
    
    .title-text h1 {
        font-size: 18px;
    }
    
    .back-btn {
        padding: 8px 12px;
        font-size: 12px;
        gap: 6px;
    }
    
    .back-btn svg {
        width: 16px;
        height: 16px;
    }
    
    .modern-table th,
    .modern-table td {
        padding: 10px 6px;
        font-size: 12px;
        vertical-align: middle;
        text-align: left;
    }
    
    .modern-table th {
        font-size: 12px;
        padding: 12px 6px;
    }
    
    .modern-table th.sortable {
        padding-right: 24px;
    }
    
    .sort-icon {
        right: 6px;
        width: 12px;
        height: 12px;
    }
    
    .guest-info {
        flex-direction: row;
        align-items: center;
        gap: 8px;
        white-space: nowrap;
    }
    
    .guest-details {
        min-width: 0;
        overflow: hidden;
    }
    
    .guest-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 120px;
        display: inline-block;
    }
    
    .guest-avatar {
        width: 28px;
        height: 28px;
        font-size: 12px;
    }
    
    .table-section {
        overflow: hidden;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }
    
    .modern-table-container {
        overflow-x: auto;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        display: block;
        flex: 1;
        min-height: 0;
    }
    
    .table-wrapper {
        min-width: 900px;
        display: table;
        width: 100%;
    }
    
    .modern-table {
        display: table;
        width: 100%;
        table-layout: auto;
    }
    
    .modern-table th,
    .modern-table td {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    
    .modern-table th {
        white-space: nowrap;
    }
    
    .payment-id,
    .mobile-cell,
    .amount-cell,
    .period-cell,
    .date-cell,
    .actions-cell {
        white-space: nowrap;
    }
}

@media (max-width: 480px) {
    .header-section {
        padding: 10px;
        margin-top: 8px;
    }
    
    .header-content {
        gap: 6px;
    }
    
    .title-icon {
        width: 32px;
        height: 32px;
    }
    
    .logo-image {
        width: 18px;
        height: 18px;
    }
    
    .title-text h1 {
        font-size: 16px;
    }
    
    .back-btn {
        padding: 6px 10px;
        font-size: 12px;
        gap: 4px;
    }
    
    .back-btn svg {
        width: 14px;
        height: 14px;
    }
    
    .filters-section {
        padding: 10px;
        gap: 10px;
    }
    
    .search-input {
        padding: 10px 12px 10px 40px;
        font-size: 14px;
    }
    
    .filter-select {
        padding: 10px 12px;
        font-size: 12px;
        min-width: 120px;
    }
    
    /* Stats Section - Proper Alignment */
    .stats-section {
        padding: 12px;
        gap: 10px;
        grid-template-columns: 1fr;
        justify-items: stretch;
    }
    
    .stat-card {
        padding: 16px;
        gap: 12px;
        align-items: center;
        justify-content: flex-start;
        text-align: left;
    }
    
    .stat-icon {
        width: 40px;
        height: 40px;
        flex-shrink: 0;
    }
    
    .stat-icon svg {
        width: 20px;
        height: 20px;
    }
    
    .stat-icon .logo-image {
        width: 20px;
        height: 20px;
    }
    
    .stat-content {
        flex: 1;
        min-width: 0;
        text-align: left;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }
    
    .stat-content h3 {
        font-size: 20px;
        margin: 0 0 2px 0;
        word-break: break-word;
        text-align: left;
        width: 100%;
    }
    
    .stat-content p {
        font-size: 12px;
        margin: 0;
        text-align: left;
        width: 100%;
    }
    
    .table-header {
        padding: 12px;
    }
    
    .table-header h2 {
        font-size: 16px;
    }
    
    .export-btn {
        padding: 8px 12px;
        font-size: 12px;
    }
    
    .export-btn svg {
        width: 16px;
        height: 16px;
    }
    
    /* Table - Show all rows without vertical scroll */
    .table-section {
        flex: 0 1 auto;
        min-height: auto;
        display: flex;
        flex-direction: column;
        overflow: visible;
        background: white;
    }
    
    .modern-table-container {
        overflow-x: auto;
        overflow-y: visible;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        flex: 0 1 auto;
        min-height: auto;
        max-height: none;
        height: auto;
        position: relative;
    }
    
    .table-wrapper {
        display: table;
    }
    
    .modern-table tbody {
        display: table-row-group;
    }
    
    .modern-table tr {
        display: table-row;
    }
    
    .table-wrapper {
        min-width: 850px;
        display: table;
        width: 100%;
    }
    
    .modern-table {
        display: table;
        width: 100%;
        table-layout: auto;
    }
    
    .modern-table th {
        padding: 10px 8px;
        font-size: 12px;
        white-space: nowrap;
        vertical-align: middle;
        text-align: left;
    }
    
    .modern-table th.sortable {
        padding-right: 24px;
    }
    
    .modern-table td {
        padding: 10px 8px;
        font-size: 12px;
        white-space: nowrap;
        vertical-align: middle;
        text-align: left;
    }
    
    .sort-icon {
        width: 12px;
        height: 12px;
        right: 8px;
    }
    
    .guest-avatar {
        width: 24px;
        height: 24px;
        font-size: 12px;
    }
    
    .guest-info {
        gap: 6px;
    }
    
    .guest-name {
        max-width: 100px;
        font-size: 12px;
    }
    
    .id-badge {
        padding: 3px 8px;
        font-size: 12px;
    }
    
    .mobile-number {
        font-size: 12px;
        padding: 3px 6px;
    }
    
    .object-tag {
        padding: 3px 8px;
        font-size: 12px;
    }
    
    .amount {
        font-size: 12px;
    }
    
    .period {
        padding: 3px 8px;
        font-size: 12px;
    }
    
    .payment-mode {
        font-size: 12px;
    }
    
    .modern-status-badge {
        padding: 4px 8px;
        font-size: 12px;
    }
    
    .status-dot {
        width: 4px;
        height: 4px;
    }
    
    .modern-action-btn {
        width: 28px;
        height: 28px;
    }
    
    .modern-action-btn svg {
        width: 16px;
        height: 16px;
    }
    
    .modern-pagination {
        padding: 12px;
        gap: 12px;
    }
    
    .pagination-info {
        font-size: 12px;
    }
    
    .pagination-arrow {
        width: 32px;
        height: 32px;
    }
    
    .pagination-arrow svg {
        width: 16px;
        height: 16px;
    }
    
    .page-btn {
        width: 32px;
        height: 32px;
        font-size: 12px;
    }
    
    .empty-illustration-img {
        width: 150px;
        height: 150px;
    }
}
</style>